/* eslint-disable no-unused-vars */
import React, { useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import html2canvas from "html2canvas";
import {
  X,
  Link2,
  Download,
  Check,
  Share2,
  QrCode,
  Gift,
  Users,
} from "lucide-react";
import logo from "../../assets/images/pebble-logo.png";

// ─── Share targets ────────────────────────────────────────────────────────────

const buildShareTargets = (url, referralCode) => {
  const headline = `🎉 Join me on Pebble — the platform built for event organizers!`;
  const body = `Use my referral code ${referralCode} when you sign up and we both get rewarded. Manage your events, vendors, ticketing, and more — all in one place.`;
  const shortText = `${headline} Use code ${referralCode} → ${url}`;

  return [
    {
      id: "x",
      label: "X / Twitter",
      color: "bg-black hover:bg-gray-800",
      icon: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      ),
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shortText)}`,
    },
    {
      id: "facebook",
      label: "Facebook",
      color: "bg-[#1877F2] hover:bg-[#166fe5]",
      icon: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      ),
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(headline)}`,
    },
    {
      id: "whatsapp",
      label: "WhatsApp",
      color: "bg-[#25D366] hover:bg-[#20bd5a]",
      icon: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      ),
      href: `https://wa.me/?text=${encodeURIComponent(shortText)}`,
    },
    {
      id: "linkedin",
      label: "LinkedIn",
      color: "bg-[#0A66C2] hover:bg-[#095db2]",
      icon: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
      ),
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    },
  ];
};

// ─── Referral Preview Card ────────────────────────────────────────────────────

const ReferralPreviewCard = React.forwardRef(({ referralCode, referralUrl }, ref) => (
  <div
    ref={ref}
    className="relative w-full rounded-xl overflow-hidden bg-white border border-gray-100 shadow-md"
    style={{ fontFamily: "Inter, sans-serif" }}
  >
    {/* Header gradient */}
    <div className="relative bg-gradient-to-br from-[#B84141] via-[#c94f4f] to-[#a03535] px-5 pt-5 pb-8">
      <div className="flex items-center gap-2 mb-3">
        <img src={logo} alt="Pebble" className="h-7 w-auto brightness-0 invert" />
      </div>
      <p className="text-white/90 text-xs font-medium tracking-wide uppercase mb-1">
        You're invited
      </p>
      <h2 className="text-white text-lg font-bold leading-tight">
        Join the platform built for<br />event organizers
      </h2>
      {/* Decorative circle */}
      <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-white/10 rounded-full" />
      <div className="absolute -bottom-10 right-10 w-16 h-16 bg-white/10 rounded-full" />
    </div>

    {/* Referral code badge */}
    <div className="flex justify-center -mt-5 mb-4 relative z-10">
      <div className="flex items-center gap-3 bg-white rounded-xl shadow-md border border-gray-100 px-5 py-3">
        <Gift className="w-4 h-4 text-[#B84141] flex-shrink-0" />
        <div>
          <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Referral Code</p>
          <p className="text-xl font-mono font-bold text-gray-900 tracking-widest">{referralCode}</p>
        </div>
      </div>
    </div>

    {/* Benefits */}
    <div className="px-4 pb-4 space-y-2">
      {[
        { icon: "✓", text: "Manage events, vendors & ticketing in one place" },
        { icon: "✓", text: "Get rewarded — you both earn when they subscribe" },
        { icon: "✓", text: "Start free, no credit card required" },
      ].map((item, i) => (
        <div key={i} className="flex items-start gap-2 text-xs text-gray-600">
          <span className="text-green-500 font-bold mt-0.5 flex-shrink-0">{item.icon}</span>
          <span>{item.text}</span>
        </div>
      ))}
    </div>

    {/* CTA footer */}
    <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
      <span className="text-xs text-gray-400 truncate max-w-[60%] font-mono">{referralUrl}</span>
      <span className="text-xs font-semibold text-[#B84141] flex-shrink-0">Sign up free →</span>
    </div>
  </div>
));
ReferralPreviewCard.displayName = "ReferralPreviewCard";

// ─── Main Modal ───────────────────────────────────────────────────────────────

const ShareReferralModal = ({ isOpen, onClose, referralCode, referralUrl }) => {
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [activeView, setActiveView] = useState("share"); // "share" | "qr"
  const [downloadingQR, setDownloadingQR] = useState(false);
  const [downloadingCard, setDownloadingCard] = useState(false);
  const previewRef = useRef(null);
  const qrRef = useRef(null);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2500);
    } catch { /* ignore */ }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(referralCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2500);
    } catch { /* ignore */ }
  };

  const nativeShare = async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({
        title: "Join me on Pebble!",
        text: `Use my referral code ${referralCode} when you sign up as an event organizer on Pebble and we both get rewarded!`,
        url: referralUrl,
      });
    } catch { copyLink(); }
  };

  const downloadQR = () => {
    if (!qrRef.current) return;
    setDownloadingQR(true);
    try {
      const canvas = qrRef.current.querySelector("canvas");
      if (!canvas) return;
      const size = canvas.width;
      const padding = 24;
      const labelHeight = 52;
      const out = document.createElement("canvas");
      out.width = size + padding * 2;
      out.height = size + padding * 2 + labelHeight;
      const ctx = out.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, out.width, out.height);
      ctx.drawImage(canvas, padding, padding);
      ctx.fillStyle = "#111827";
      ctx.font = "bold 15px Inter, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("Join me on Pebble!", out.width / 2, size + padding * 2 + 14);
      ctx.fillStyle = "#6B7280";
      ctx.font = "12px Inter, sans-serif";
      ctx.fillText(`Use code ${referralCode} when signing up`, out.width / 2, size + padding * 2 + 34);
      const link = document.createElement("a");
      link.download = `pebble-referral-${referralCode}.png`;
      link.href = out.toDataURL("image/png");
      link.click();
    } finally { setDownloadingQR(false); }
  };

  const downloadShareCard = async () => {
    if (!previewRef.current) return;
    setDownloadingCard(true);
    try {
      const canvas = await html2canvas(previewRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
      const link = document.createElement("a");
      link.download = `pebble-referral-${referralCode}-card.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (err) {
      console.error("Error generating share card:", err);
    } finally { setDownloadingCard(false); }
  };

  if (!isOpen) return null;

  const shareTargets = buildShareTargets(referralUrl, referralCode);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl flex flex-col max-h-[92vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center">
              <Users className="w-4 h-4 text-[#B84141]" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Invite Organizers</h2>
              <p className="text-xs text-gray-500">Share your referral code and both get rewarded</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tab toggle */}
        <div className="flex gap-1 px-5 pt-4 pb-0 flex-shrink-0">
          <button
            onClick={() => setActiveView("share")}
            className={`flex-1 py-2 text-sm font-medium rounded-lg transition ${
              activeView === "share" ? "bg-gray-900 text-white" : "text-gray-500 hover:bg-gray-100"
            }`}
          >
            Share Link
          </button>
          <button
            onClick={() => setActiveView("qr")}
            className={`flex-1 py-2 text-sm font-medium rounded-lg transition flex items-center justify-center gap-1.5 ${
              activeView === "qr" ? "bg-gray-900 text-white" : "text-gray-500 hover:bg-gray-100"
            }`}
          >
            <QrCode className="w-3.5 h-3.5" />
            QR Code
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {activeView === "share" ? (
            <>
              {/* Preview card */}
              <ReferralPreviewCard ref={previewRef} referralCode={referralCode} referralUrl={referralUrl} />

              {/* Code + Link copy row */}
              <div className="grid grid-cols-2 gap-2">
                {/* Copy code */}
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Code</p>
                    <p className="text-base font-mono font-bold text-gray-900 truncate">{referralCode}</p>
                  </div>
                  <button
                    onClick={copyCode}
                    className={`flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-lg transition ${
                      copiedCode ? "bg-green-100 text-green-700" : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    {copiedCode ? <><Check className="w-3 h-3" />Copied!</> : "Copy"}
                  </button>
                </div>
                {/* Copy link */}
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Link</p>
                    <p className="text-xs text-gray-500 truncate font-mono">{referralUrl}</p>
                  </div>
                  <button
                    onClick={copyLink}
                    className={`flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-lg transition ${
                      copiedLink ? "bg-green-100 text-green-700" : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    {copiedLink ? <><Check className="w-3 h-3" />Copied!</> : "Copy"}
                  </button>
                </div>
              </div>

              {/* Social share buttons */}
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">Share to</p>
                <div className="grid grid-cols-2 gap-2">
                  {shareTargets.map((target) => (
                    <a
                      key={target.id}
                      href={target.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center gap-2 px-3 py-2.5 text-white text-sm font-medium rounded-xl transition ${target.color}`}
                    >
                      {target.icon}
                      {target.label}
                    </a>
                  ))}
                  {typeof navigator !== "undefined" && navigator.share && (
                    <button
                      onClick={nativeShare}
                      className="flex items-center gap-2 px-3 py-2.5 text-gray-700 text-sm font-medium rounded-xl bg-gray-100 hover:bg-gray-200 transition col-span-2"
                    >
                      <Share2 className="w-4 h-4" />
                      More sharing options…
                    </button>
                  )}
                </div>
              </div>

              {/* Download share image */}
              <button
                onClick={downloadShareCard}
                disabled={downloadingCard}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-200 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 transition disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                {downloadingCard ? "Generating…" : "Download Share Image"}
              </button>
            </>
          ) : (
            /* QR Code view */
            <div className="flex flex-col items-center gap-5">
              <p className="text-sm text-gray-500 text-center">
                Let other organizers scan to sign up with your code automatically applied.
              </p>

              <div
                ref={qrRef}
                className="p-5 bg-white border-2 border-gray-200 rounded-2xl shadow-sm flex flex-col items-center gap-3"
              >
                <QRCodeCanvas
                  value={referralUrl}
                  size={200}
                  bgColor="#ffffff"
                  fgColor="#111827"
                  level="H"
                  imageSettings={{
                    src: `${window.location.origin}/favicon.ico`,
                    x: undefined,
                    y: undefined,
                    height: 28,
                    width: 28,
                    excavate: true,
                  }}
                />
                <p className="text-xs font-semibold text-gray-700 text-center">Join me on Pebble!</p>
                <p className="text-xs text-gray-400 text-center">
                  Code <span className="font-mono font-bold text-gray-700">{referralCode}</span> applied automatically
                </p>
              </div>

              {/* Copy row */}
              <div className="w-full grid grid-cols-2 gap-2">
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Code</p>
                    <p className="text-base font-mono font-bold text-gray-900">{referralCode}</p>
                  </div>
                  <button onClick={copyCode} className={`flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-lg transition ${copiedCode ? "bg-green-100 text-green-700" : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-100"}`}>
                    {copiedCode ? <><Check className="w-3 h-3" />Copied!</> : "Copy"}
                  </button>
                </div>
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Link</p>
                    <p className="text-xs text-gray-500 truncate font-mono">{referralUrl}</p>
                  </div>
                  <button onClick={copyLink} className={`flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-lg transition ${copiedLink ? "bg-green-100 text-green-700" : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-100"}`}>
                    {copiedLink ? <><Check className="w-3 h-3" />Copied!</> : "Copy"}
                  </button>
                </div>
              </div>

              <button
                onClick={downloadQR}
                disabled={downloadingQR}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-900 text-white text-sm font-semibold rounded-xl hover:bg-gray-800 transition disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                {downloadingQR ? "Generating…" : "Download QR Code"}
              </button>

              <div className="w-full bg-amber-50 rounded-xl p-3 text-xs text-amber-700 space-y-1">
                <p className="font-semibold">💡 Easy ways to share</p>
                <ul className="space-y-0.5 list-disc list-inside text-amber-600">
                  <li>Print on business cards or handouts</li>
                  <li>Display at industry events or conferences</li>
                  <li>Add to your email signature</li>
                  <li>Share in event organizer communities or groups</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareReferralModal;
